import React, { useState } from "react";
import styled from "styled-components";
import type { CategoryHierarchy, HierarchyNode } from "../types/Category.ts";
import { FluentEmojiFileFolderIcon } from "../icons/fluentEmojiFlatBundled.tsx";
import { LucideChevronDownIcon, LucideChevronRightIcon } from "../icons/lucideBundled.tsx";

const TreeContainer = styled.div`
  margin-top: 1rem;
`;

const TREE_INDENT = "0.75rem";

const TreeNode = styled.div`
  margin-bottom: 0.25rem;
`;

const TreeNodeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-radius: 4px;
`;

const ExpandIconSpacer = styled.span`
  width: 16px;
  flex-shrink: 0;
`;

const ExpandIconButton = styled.button`
  width: 16px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  color: #666;
  cursor: pointer;
  line-height: 1;

  svg {
    display: block;
  }

  .dark-mode & {
    color: #999;
  }

  &:hover {
    color: #001f52;
  }

  .dark-mode &:hover {
    color: #e0e0e0;
  }
`;

const NodeSelectTrigger = styled.button<{ $isActive?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  font-size: 0.9rem;
  color: ${props => props.$isActive ? "#001f52" : "#666"};
  cursor: pointer;
  text-align: left;
  font-weight: ${props => props.$isActive ? 600 : 400};
  transition: color 0.2s ease;

  .dark-mode & {
    color: ${props => props.$isActive ? "#e0e0e0" : "#999"};
  }

  &:hover {
    color: #64da4c;

    .dark-mode & {
      color: #64da4c;
    }
  }
`;

const NodeIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 0.25rem;

  svg {
    display: block;
    width: 1rem;
    height: 1rem;
  }
`;

const NodeLabel = styled.span`
  color: inherit;
  font-weight: inherit;
`;

const NodeChildren = styled.div<{ $expanded: boolean }>`
  padding-left: ${TREE_INDENT};
  max-height: ${props => props.$expanded ? '1000px' : '0'};
  overflow: hidden;
  opacity: ${props => props.$expanded ? '1' : '0'};
  transition: opacity 0.2s ease, max-height 0.3s ease;
`;

interface CategoryHierarchyTreeProps {
  hierarchy: CategoryHierarchy;
  currentPath?: string[];
  onSelectPath?: (path: string[]) => void;
}

const getNodeSegment = (nodeId: number) => `node-${nodeId}`;

const pathsEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((segment, index) => segment === b[index]);

export default function CategoryHierarchyTree({
  hierarchy,
  currentPath = [],
  onSelectPath,
}: CategoryHierarchyTreeProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const renderNode = (node: HierarchyNode, parentPath: string[] = []) => {
    const nodeKey = getNodeSegment(node.id);
    const path = [...parentPath, nodeKey];
    const children = node.children || [];
    const hasChildren = children.length > 0;
    const isExpanded = expandedNodes.has(nodeKey);
    const isSelected = pathsEqual(path, currentPath);

    return (
      <TreeNode key={node.id}>
        <TreeNodeHeader>
          {hasChildren ? (
            <ExpandIconButton
              type="button"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? "Skjul undermapper" : "Vis undermapper"}
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(nodeKey);
              }}
            >
              {isExpanded ? (
                <LucideChevronDownIcon size={16} />
              ) : (
                <LucideChevronRightIcon size={16} />
              )}
            </ExpandIconButton>
          ) : (
            <ExpandIconSpacer aria-hidden />
          )}
          <NodeSelectTrigger
            type="button"
            $isActive={isSelected}
            onClick={() => onSelectPath?.(path)}
          >
            <NodeIcon aria-hidden>
              <FluentEmojiFileFolderIcon size={16} />
            </NodeIcon>
            <NodeLabel>{node.name}</NodeLabel>
          </NodeSelectTrigger>
        </TreeNodeHeader>
        {hasChildren && (
          <NodeChildren $expanded={isExpanded}>
            {children.map((child) => renderNode(child, path))}
          </NodeChildren>
        )}
      </TreeNode>
    );
  };

  const topLevelNodes = hierarchy.children || [];

  if (topLevelNodes.length === 0) {
    return (
      <TreeContainer>
        <div style={{ color: "#666", fontSize: "0.9rem" }} className="dark-mode-text-secondary">
          Ingen mappestruktur endnu.
        </div>
      </TreeContainer>
    );
  }

  return (
    <TreeContainer>
      {topLevelNodes.map((node) => renderNode(node))}
    </TreeContainer>
  );
}
